import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@bynder/localization';
import { Box, Button, CardGrid, Flex, Form, Input, List, ModalBase } from '@bynder/design-system';
import { IconArrowLeft, IconArrowRight, IconSearch } from '@bynder/icons';
import ComponentCollectionService, { ComponentCollection } from 'packages/services/ComponentCollectionService';
import ComponentService, { PartialComponent } from 'packages/services/ComponentService';
import useDebouncedState from 'packages/hooks/useDebouncedState';
import { ListItemContent, StyledDivider, StyledModalContainer } from './styled';
import ComponentCard from './ComponentCard';
import CollectionMenu from './CollectionMenu';
import OverviewFilters from './OverviewFilters';

const COLLECTIONS_PAGE_SIZE = 50;
const COMPONENTS_PAGE_SIZE = 20;

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onApply: (component: PartialComponent, isUniqueContentProperties: boolean) => void;
}

interface EnrichedComponentCollection extends ComponentCollection {
    components: PartialComponent[];
    totalComponents: number;
}

const ComponentOverview = ({ isOpen, onClose, onApply }: Props) => {
    const { translate } = useTranslate();

    const {
        state: searchValue,
        debouncedState: debouncedSearchValue,
        setState: setSearchValue,
    } = useDebouncedState('', 500);

    const [collections, setCollections] = useState<EnrichedComponentCollection[]>([]);
    const [collectionsPage, setCollectionsPage] = useState(0);
    const [hasMoreCollections, setHasMoreCollections] = useState(true);
    const [isCollectionsLoading, setIsCollectionsLoading] = useState(false);
    const [isCollectionsLoadingVisible, setIsCollectionsLoadingVisible] = useState(false);

    const [components, setComponents] = useState<PartialComponent[]>([]);
    const [componentsPage, setComponentsPage] = useState(0);
    const [componentsTotal, setComponentsTotal] = useState(0);
    const [hasMoreComponents, setHasMoreComponents] = useState(true);
    const [isComponentsLoading, setIsComponentsLoading] = useState(false);
    const [isComponentsLoadingVisible, setIsComponentsLoadingVisible] = useState(false);

    const [selectedCollection, setSelectedCollection] = useState<EnrichedComponentCollection | null>(null);

    const componentOverviewContainerRef = useRef(null);

    const observerCollections = useRef<IntersectionObserver>();
    const lastCollectionElementRef = useCallback(
        (node: HTMLDivElement) => {
            if (isCollectionsLoading) {
                return;
            }

            if (observerCollections.current) {
                observerCollections.current.disconnect();
            }

            observerCollections.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMoreCollections) {
                    setCollectionsPage((prevPage) => prevPage + 1);
                }
            });

            if (node) {
                observerCollections.current.observe(node);
            }
        },
        [isCollectionsLoading, hasMoreCollections],
    );

    const observerComponents = useRef<IntersectionObserver>();
    const lastComponentElementRef = useCallback(
        (node: HTMLDivElement) => {
            if (isComponentsLoading) {
                return;
            }

            if (observerComponents.current) {
                observerComponents.current.disconnect();
            }

            observerComponents.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMoreComponents) {
                    setComponentsPage((prevPage) => prevPage + 1);
                }
            });

            if (node) {
                observerComponents.current.observe(node);
            }
        },
        [isComponentsLoading, hasMoreComponents],
    );

    useEffect(() => {
        if (isOpen) {
            setSearchValue('');
            setCollectionsPage(0);
            setCollections([]);
            setHasMoreCollections(true);
            setSelectedCollection(null);
            setComponents([]);
            setComponentsPage(0);
            setComponentsTotal(0);
            setHasMoreComponents(true);
        }
    }, [isOpen]);

    useEffect(() => {
        let isMounted = true;
        let loadingTimer: ReturnType<typeof setTimeout>;

        if (isOpen && !selectedCollection) {
            setIsCollectionsLoading(true);
            loadingTimer = setTimeout(() => {
                if (isMounted) {
                    setIsCollectionsLoadingVisible(true);
                }
            }, 200);

            ComponentCollectionService.getComponentCollections({
                size: COLLECTIONS_PAGE_SIZE,
                page: collectionsPage,
                presetSearch: debouncedSearchValue,
            })
                .then(async (response) => {
                    if (isMounted) {
                        if (response.status !== 200) {
                            return;
                        }

                        const collectionContent = response.json.content;

                        const enrichedCollections = await Promise.all(
                            collectionContent.map(async (collection) => {
                                const componentsResponse = await ComponentService.getComponents({
                                    size: 4,
                                    page: 0,
                                    collectionId: collection.id,
                                    search: debouncedSearchValue,
                                });

                                if (componentsResponse.status !== 200) {
                                    return {
                                        ...collection,
                                        components: [],
                                        totalComponents: 0,
                                    };
                                }

                                return {
                                    ...collection,
                                    components: componentsResponse.json.content,
                                    totalComponents: componentsResponse.json.totalElements,
                                };
                            }),
                        );

                        setCollections((prev) =>
                            collectionsPage === 0 ? enrichedCollections : [...prev, ...enrichedCollections],
                        );
                        setHasMoreCollections(!response.json.last);
                    }
                })
                .finally(() => {
                    if (isMounted) {
                        clearTimeout(loadingTimer);
                        setIsCollectionsLoadingVisible(false);
                        setIsCollectionsLoading(false);
                    }
                });
        }

        return () => {
            isMounted = false;
            clearTimeout(loadingTimer);
        };
    }, [isOpen, debouncedSearchValue, collectionsPage, selectedCollection]);

    useEffect(() => {
        let isMounted = true;
        let loadingTimer: ReturnType<typeof setTimeout>;

        if (isOpen && selectedCollection) {
            setIsComponentsLoading(true);
            loadingTimer = setTimeout(() => {
                if (isMounted) {
                    setIsComponentsLoadingVisible(true);
                }
            }, 200);

            ComponentService.getComponents({
                size: COMPONENTS_PAGE_SIZE,
                page: componentsPage,
                search: debouncedSearchValue,
                collectionId: selectedCollection.id,
            })
                .then((response) => {
                    if (isMounted) {
                        if (response.status !== 200) {
                            return;
                        }

                        const newComponents = response.json.content;
                        setComponents((prev) => (componentsPage === 0 ? newComponents : [...prev, ...newComponents]));
                        setComponentsTotal(response.json.totalElements);
                        setHasMoreComponents(!response.json.last);
                    }
                })
                .finally(() => {
                    if (isMounted) {
                        clearTimeout(loadingTimer);
                        setIsComponentsLoadingVisible(false);
                        setIsComponentsLoading(false);
                    }
                });
        }

        return () => {
            isMounted = false;
            clearTimeout(loadingTimer);
        };
    }, [isOpen, debouncedSearchValue, componentsPage, selectedCollection]);

    return (
        <>
            <StyledModalContainer ref={componentOverviewContainerRef} />
            <ModalBase isOpen={isOpen} onClose={onClose} container={componentOverviewContainerRef.current}>
                <ModalBase.Header title={translate('components.overview.title')} />
                <ModalBase.Content>
                    <Form>
                        <Form.Group>
                            <Form.InputRow>
                                <Input
                                    value={searchValue}
                                    onChange={(value) => {
                                        setSearchValue(value);
                                        setCollectionsPage(0);
                                        setCollections([]);
                                        setHasMoreCollections(true);
                                    }}
                                    type="text"
                                    placeholder={translate('components.overview.search.input.placeholder')}
                                    aria-label={translate('components.overview.search.input.label')}
                                    icon={<IconSearch />}
                                    hasClearButton
                                />
                                <OverviewFilters />
                            </Form.InputRow>
                        </Form.Group>
                    </Form>
                    <StyledDivider />
                    <List>
                        {!selectedCollection ? (
                            <>
                                {collections.length === 0 && !isCollectionsLoading && (
                                    <p>{translate('components.overview.collections.noResults')}</p>
                                )}
                                {collections.map((collection) => (
                                    <React.Fragment key={collection.id}>
                                        <List.Item
                                            subtext={translate('components.overview.collections.number', {
                                                count: collection.totalComponents,
                                            })}
                                            onClick={() => setSelectedCollection(collection)}
                                            rightElements={<IconArrowRight />}
                                        >
                                            {collection.name}
                                        </List.Item>
                                        <Box paddingTop="4" paddingBottom="4">
                                            <CardGrid columnMinWidth={171}>
                                                {collection.components.map((component) => (
                                                    <ComponentCard
                                                        key={component.id}
                                                        component={component}
                                                        collectionName={collection.name}
                                                        onClick={(isUniqueContentProperties) => {
                                                            onApply(component, isUniqueContentProperties);
                                                        }}
                                                    />
                                                ))}
                                            </CardGrid>
                                        </Box>
                                    </React.Fragment>
                                ))}
                                {isCollectionsLoadingVisible && (
                                    <Flex justifyContent="center">
                                        <Button isLoading variant="clean" aria-label={translate('pages.loading')} />
                                    </Flex>
                                )}
                                {collections.length > 0 && (
                                    <div ref={lastCollectionElementRef} style={{ height: 1, width: '100%' }} />
                                )}
                            </>
                        ) : (
                            <>
                                <ListItemContent
                                    key={selectedCollection.id}
                                    subtext={translate('components.overview.collections.number', {
                                        count: componentsTotal,
                                    })}
                                    onClick={() => {
                                        setSelectedCollection(null);
                                        setComponents([]);
                                        setComponentsPage(0);
                                        setHasMoreComponents(true);
                                    }}
                                    thumbnail={<IconArrowLeft />}
                                    rightElements={<CollectionMenu onEdit={() => {}} onDelete={() => {}} />}
                                >
                                    {selectedCollection.name}
                                </ListItemContent>
                                <List>
                                    {components.length === 0 && !isComponentsLoading && (
                                        <p>{translate('components.overview.components.noResults')}</p>
                                    )}
                                    <CardGrid columnMinWidth={171}>
                                        {components.map((component) => (
                                            <ComponentCard
                                                key={component.id}
                                                component={component}
                                                collectionName={selectedCollection.name}
                                                onClick={(isUniqueContentProperties) => {
                                                    onApply(component, isUniqueContentProperties);
                                                }}
                                            />
                                        ))}
                                    </CardGrid>
                                    {isComponentsLoadingVisible && (
                                        <Flex justifyContent="center">
                                            <Button isLoading variant="clean" aria-label={translate('pages.loading')} />
                                        </Flex>
                                    )}
                                    {components.length > 0 && (
                                        <div ref={lastComponentElementRef} style={{ height: 1, width: '100%' }} />
                                    )}
                                </List>
                            </>
                        )}
                    </List>
                </ModalBase.Content>
            </ModalBase>
        </>
    );
};

export default ComponentOverview;
